import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Users,
  FileText,
  Clock,
  Bot,
  CheckCircle,
  TrendingUp,
  DollarSign,
  MessageSquare,
  Mail,
  ChevronDown,
  ChevronUp,
  CalendarCheck,
  Send,
  UserCheck
} from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import AdminMessages from '../../components/workday-client/AdminMessages';
import { getBillingState, BillingSubscription } from '../../lib/billingState';

interface DashboardCard {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  count?: number;
  status?: string;
  href: string;
  color: string;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

interface RecentActivity {
  id: string;
  type: 'application' | 'timesheet' | 'job_posting' | 'contract';
  title: string;
  description: string;
  timestamp: string;
  color: string;
}

export default function WorkdayClientDashboard() {
  const { theme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const adminPlanParam = searchParams.get('adminPlan');
  // Appends adminPlan to internal hrefs so the param survives navigation within the iframe
  const withPlan = (href: string) => {
    if (!adminPlanParam || href.startsWith('http://') || href.startsWith('https://')) return href;
    const sep = href.includes('?') ? '&' : '?';
    return `${href}${sep}adminPlan=${adminPlanParam}`;
  };

  // Apply light theme synchronously before first render to prevent dark flash on Safari
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', 'light');
  }
  const [messagesVisible, setMessagesVisible] = useState<boolean>(() => {
    try { return localStorage.getItem('client_msgs_visible') === 'true'; } catch { return false; }
  });
  const [userProfile, setUserProfile] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [dashboardStats, setDashboardStats] = useState({
    activeJobs: 0,
    activeContracts: 0,
    pendingTimesheets: 0,
    totalSpent: 0,
    totalConsultants: 0,
    interviewRequests: 0,
    pendingApplications: 0,
    pipelineInterviews: 0,
    pipelineOffers: 0,
    pipelineHired: 0,
  });

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      fetchDashboardStats();
      fetchRecentActivity();

      const channel = supabase
        .channel('dashboard_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'workday_client_job_postings',
            filter: `customer_id=eq.${user.id}`
          },
          () => {
            fetchDashboardStats();
            fetchRecentActivity();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'interview_requests',
            filter: `client_id=eq.${user.id}`
          },
          () => {
            fetchDashboardStats();
            fetchRecentActivity();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'job_applications',
          },
          () => {
            fetchDashboardStats();
            fetchRecentActivity();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id]);


  const fetchUserProfile = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('workday_client_profiles')
        .select(`
          *,
          companies (
            id,
            name,
            industry,
            size
          )
        `)
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      setUserProfile(data);

      // pending_checkout = user started checkout during signup but never completed it.
      // We no longer redirect back to checkout on subsequent logins — the user already
      // chose free by navigating away or cancelling. Just clear the flag so it stops
      // appearing as pending.
      if (data?.pending_checkout) {
        const { data: subRow } = await supabase
          .from('billing_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        const state = getBillingState(subRow as BillingSubscription | null);
        if (state === 'FREE' || state === 'CANCELLED') {
          await supabase
            .from('workday_client_profiles')
            .update({ pending_checkout: false })
            .eq('id', user.id);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchDashboardStats = async () => {
    if (!user?.id) return;

    try {
      // Fetch job postings for this client (using workday_client_job_postings table)
      const { count: jobCount } = await supabase
        .from('workday_client_job_postings')
        .select('*', { count: 'exact' })
        .eq('customer_id', user.id);

      // Fetch active contracts for this client
      const { data: contractsData, count: contractCount } = await supabase
        .from('contracts')
        .select('consultant_id', { count: 'exact' })
        .eq('customer_id', user.id)
        .eq('status', 'active');

      // Fetch pending timesheets for this client
      const { count: timesheetCount } = await supabase
        .from('timesheets')
        .select('*', { count: 'exact' })
        .eq('customer_id', user.id)
        .eq('status', 'submitted');

      // Count unique consultants from active contracts
      const uniqueConsultantIds = contractsData
        ? [...new Set(contractsData.map(c => c.consultant_id))]
        : [];

      // Fetch interview requests count
      const { count: interviewCount } = await supabase
        .from('interview_requests')
        .select('*', { count: 'exact' })
        .eq('client_id', user.id)
        .eq('status', 'pending');

      // Fetch pending (unreviewed) job applications across all client's job postings
      const { data: myJobs } = await supabase
        .from('workday_client_job_postings')
        .select('id')
        .eq('customer_id', user.id);
      let appCount = 0;
      if (myJobs && myJobs.length > 0) {
        const { count } = await supabase
          .from('job_applications')
          .select('*', { count: 'exact' })
          .in('job_posting_id', myJobs.map(j => j.id))
          .eq('status', 'pending');
        appCount = count || 0;
      }

      // Fetch pipeline stage counts
      const { data: pipelineRows } = await supabase
        .from('candidate_pipeline_entries')
        .select('stage')
        .eq('client_id', user.id)
        .in('stage', ['interview', 'offer', 'hired']);

      const pipelineInterviews = pipelineRows?.filter(r => r.stage === 'interview').length || 0;
      const pipelineOffers = pipelineRows?.filter(r => r.stage === 'offer').length || 0;
      const pipelineHired = pipelineRows?.filter(r => r.stage === 'hired').length || 0;

      setDashboardStats({
        activeJobs: jobCount || 0,
        activeContracts: contractCount || 0,
        pendingTimesheets: timesheetCount || 0,
        totalSpent: 0,
        totalConsultants: uniqueConsultantIds.length,
        interviewRequests: interviewCount || 0,
        pendingApplications: appCount,
        pipelineInterviews,
        pipelineOffers,
        pipelineHired,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    if (!user?.id) return;

    try {
      const activities: RecentActivity[] = [];

      // Fetch recent job postings from workday_client_job_postings
      const { data: jobs } = await supabase
        .from('workday_client_job_postings')
        .select('id, job_title, created_at')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(2);

      if (jobs) {
        jobs.forEach(job => {
          activities.push({
            id: `job-${job.id}`,
            type: 'job_posting',
            title: 'Job posting published',
            description: `${job.job_title}`,
            timestamp: job.created_at,
            color: 'bg-blue-500'
          });
        });
      }

      // Fetch recent approved timesheets
      const { data: timesheets } = await supabase
        .from('timesheets')
        .select(`
          id,
          hours,
          created_at,
          consultant_profiles (first_name, last_name)
        `)
        .eq('customer_id', user.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(2);

      if (timesheets) {
        timesheets.forEach((timesheet: any) => {
          const consultantName = timesheet.consultant_profiles
            ? `${timesheet.consultant_profiles.first_name} ${timesheet.consultant_profiles.last_name}`
            : 'Consultant';
          activities.push({
            id: `timesheet-${timesheet.id}`,
            type: 'timesheet',
            title: `Timesheet approved for ${consultantName}`,
            description: `${timesheet.hours} hours`,
            timestamp: timesheet.created_at,
            color: 'bg-green-500'
          });
        });
      }

      // Fetch recent job applications for the client's postings
      const { data: myJobIds } = await supabase
        .from('workday_client_job_postings')
        .select('id, job_title')
        .eq('customer_id', user.id);
      if (myJobIds && myJobIds.length > 0) {
        const { data: recentApps } = await supabase
          .from('job_applications')
          .select('id, job_posting_id, applied_at, consultant_id')
          .in('job_posting_id', myJobIds.map(j => j.id))
          .order('applied_at', { ascending: false })
          .limit(3);
        if (recentApps) {
          recentApps.forEach(app => {
            const job = myJobIds.find(j => j.id === app.job_posting_id);
            activities.push({
              id: `app-${app.id}`,
              type: 'application',
              title: 'New application received',
              description: job?.job_title || 'Job posting',
              timestamp: app.applied_at,
              color: 'bg-[#40E0D0]',
            });
          });
        }
      }

      // Sort by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setRecentActivity(activities.slice(0, 5));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  const dashboardCards: DashboardCard[] = [
    {
      id: 'post-jobs',
      title: 'Post Jobs',
      icon: <Briefcase className="w-8 h-8" />,
      description: 'Post and edit job postings, share Workday issues, project requirements, budgets and upload project documents',
      count: dashboardStats.activeJobs,
      href: '/workday-client/jobs',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'workday-talent',
      title: 'Workday Talent',
      icon: <Users className="w-8 h-8" />,
      description: 'Search and filter Perplexity\'s Workday Talent pool, view consultant profiles, and track candidates through your hiring pipeline from sourcing to hire.',
      href: '/workday-client/talent',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'interview-requests',
      title: 'Interview Requests',
      icon: <MessageSquare className="w-8 h-8" />,
      description: 'View and manage your consultant interview requests',
      count: dashboardStats.interviewRequests,
      status: dashboardStats.interviewRequests > 0 ? 'Pending' : 'None',
      href: '/workday-client/interview-requests',
      color: 'from-pink-500 to-pink-600'
    },
    {
      id: 'contracts-hires',
      title: 'Contract & Hires',
      icon: <FileText className="w-8 h-8" />,
      description: 'Manage contracts, track hiring progress, and oversee consultant agreements',
      count: dashboardStats.activeContracts,
      href: '/workday-client/contracts',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'approve-time',
      title: 'Approve Time',
      icon: <Clock className="w-8 h-8" />,
      description: 'Review and approve timesheets from Workday UI Testing SMEs and Workday SME Consultants',
      count: dashboardStats.pendingTimesheets,
      status: dashboardStats.pendingTimesheets > 0 ? 'Pending Review' : 'Up to Date',
      href: '/workday-client/timesheets',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'consultant-documents',
      title: 'Consultant Documents',
      icon: <FileText className="w-8 h-8" />,
      description: 'Share onboarding materials, resources, and policies with your consultants',
      href: '/workday-client/consultant-documents',
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 'perplexity-alm',
      title: 'PerplexityALM',
      icon: <Bot className="w-8 h-8" />,
      description: 'Access PerplexityALM platform for advanced project lifecycle management',
      href: 'https://perplexityalm.com/',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      id: 'ai-assistant',
      title: 'AI Assistant',
      icon: <Bot className="w-8 h-8" />,
      description: 'Get AI-powered insights for project optimization and consultant matching',
      href: '/workday-client/ai',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];


  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-black' : 'bg-gray-50'
      }`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-black text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      <WorkdayClientHeader />

      {/* Main content */}
      <main className="pt-32 px-6 pb-6 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {userProfile?.first_name || 'Client'}!
          </h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            {userProfile?.companies?.name && `Managing Workday projects for ${userProfile.companies.name}`}
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-4">
          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-white'
          } shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Workday Consultants
                </p>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.totalConsultants}</p>
              </div>
              <Users className="w-8 h-8 text-[#40E0D0]" />
            </div>
          </div>

          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-white'
          } shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Active Jobs
                </p>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.activeJobs}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-[#40E0D0]" />
            </div>
          </div>

          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-white'
          } shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Active Contracts
                </p>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.activeContracts}</p>
              </div>
              <FileText className="w-8 h-8 text-[#40E0D0]" />
            </div>
          </div>

          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-white'
          } shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Pending Approvals
                </p>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.pendingTimesheets}</p>
              </div>
              <Clock className="w-8 h-8 text-[#40E0D0]" />
            </div>
          </div>

          <div
            className={`p-6 rounded-xl shadow-sm cursor-pointer transition-all hover:ring-2 hover:ring-[#40E0D0]/40 ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            } ${dashboardStats.pendingApplications > 0 ? 'ring-2 ring-[#40E0D0]/30' : ''}`}
            onClick={() => navigate(withPlan('/workday-client/jobs'))}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  New Applications
                </p>
                <p className={`text-2xl font-bold ${dashboardStats.pendingApplications > 0 ? 'text-[#40E0D0]' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {dashboardStats.pendingApplications}
                </p>
              </div>
              <CheckCircle className={`w-8 h-8 ${dashboardStats.pendingApplications > 0 ? 'text-[#40E0D0]' : theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
            {dashboardStats.pendingApplications > 0 && (
              <p className="text-xs text-[#40E0D0] mt-1 font-medium">Pending review →</p>
            )}
          </div>
        </div>

        {/* Pipeline Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div
            className={`p-6 rounded-xl shadow-sm cursor-pointer transition-all hover:ring-2 hover:ring-[#40E0D0]/40 ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={() => navigate(withPlan('/workday-client/talent'))}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>In Interview</p>
                <p className={`text-2xl font-bold ${dashboardStats.pipelineInterviews > 0 ? 'text-[#40E0D0]' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {dashboardStats.pipelineInterviews}
                </p>
              </div>
              <CalendarCheck className={`w-8 h-8 ${dashboardStats.pipelineInterviews > 0 ? 'text-[#40E0D0]' : theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Pipeline stage: Interview</p>
          </div>

          <div
            className={`p-6 rounded-xl shadow-sm cursor-pointer transition-all hover:ring-2 hover:ring-amber-400/40 ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={() => navigate(withPlan('/workday-client/talent'))}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Offers Out</p>
                <p className={`text-2xl font-bold ${dashboardStats.pipelineOffers > 0 ? 'text-amber-500' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {dashboardStats.pipelineOffers}
                </p>
              </div>
              <Send className={`w-8 h-8 ${dashboardStats.pipelineOffers > 0 ? 'text-amber-500' : theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Pipeline stage: Offer</p>
          </div>

          <div
            className={`p-6 rounded-xl shadow-sm cursor-pointer transition-all hover:ring-2 hover:ring-green-400/40 ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={() => navigate(withPlan('/workday-client/talent'))}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Hired</p>
                <p className={`text-2xl font-bold ${dashboardStats.pipelineHired > 0 ? 'text-green-600' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {dashboardStats.pipelineHired}
                </p>
              </div>
              <UserCheck className={`w-8 h-8 ${dashboardStats.pipelineHired > 0 ? 'text-green-600' : theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Pipeline stage: Hired</p>
          </div>
        </div>

        {/* Messages from Perplexity Team */}
        <div className="mb-8">
          <div className={`rounded-xl border overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            {/* Collapsible header */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setMessagesVisible(v => { const next = !v; try { localStorage.setItem('client_msgs_visible', String(next)); } catch {} return next; })}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setMessagesVisible(v => { const next = !v; try { localStorage.setItem('client_msgs_visible', String(next)); } catch {} return next; }); }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              className={`flex items-center justify-between px-6 py-5 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <h2 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Messages from Perplexity Team</h2>
                  <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {messagesVisible ? 'Click to hide messages' : 'Click to show messages'}
                  </p>
                </div>
              </div>
              <div className={`flex items-center gap-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                <span>{messagesVisible ? 'Hide' : 'Show'}</span>
                {messagesVisible ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>

            {/* Collapsible content */}
            {messagesVisible && (
              <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <AdminMessages hideOuterCard />
              </div>
            )}
          </div>
        </div>

        {/* Dashboard cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {dashboardCards.map((card) => {
            const isExternalLink = card.href.startsWith('http://') || card.href.startsWith('https://');
            const CardWrapper = isExternalLink ? 'a' : Link;
            const linkProps = isExternalLink
              ? { href: card.href, target: '_blank', rel: 'noopener noreferrer' }
              : { to: withPlan(card.href) };

            return (
              <CardWrapper
                key={card.id}
                {...linkProps}
                className={`block p-6 rounded-xl transition-all duration-200 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-gray-900 hover:bg-gray-800 border border-gray-700'
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${card.color}`}>
                    <div className="text-white">{card.icon}</div>
                  </div>
                  {card.count !== undefined && (
                    <span className="bg-[#40E0D0] text-black px-2 py-1 rounded-full text-sm font-semibold">
                      {card.count}
                    </span>
                  )}
                  {card.status && (
                    <span className={`px-2 py-1 rounded-full text-sm font-semibold ${
                      card.status === 'Pending Review'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {card.status}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {card.description}
                </p>
              </CardWrapper>
            );
          })}
        </div>

        {/* Recent activity section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Recent Activity</h2>
          <div className={`rounded-xl p-6 ${
            theme === 'dark'
              ? 'bg-gray-900 border border-gray-700'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.map((activity) => {
                  const timeAgo = getTimeAgo(new Date(activity.timestamp));
                  return (
                    <div key={activity.id} className="flex items-center space-x-4">
                      <div className={`w-2 h-2 ${activity.color} rounded-full`}></div>
                      <div>
                        <p className="font-medium">{activity.title}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {activity.description} - {timeAgo}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className={`w-12 h-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={`text-lg font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  No recent activity
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                  Start by posting a job or hiring consultants to see activity here
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}